import React from "react";
import { Trans, withTranslation } from "react-i18next";
import { toastr } from "react-redux-toastr";
import { FormGroup, FormControl, FormLabel } from "react-bootstrap";
import Drawer from "components/Drawer";
import Loading from "components/Loading";
import SwitchField from "components/SwitchField";
import SubmitButton from "components/SubmitButton";
import { companyService } from "services";
import { errorFormating } from "utils/utils";
import { isHaddockEnvironment } from "utils/documentUtils";
import { onboardingStore } from "components/Onboarding/store/onboardingStore";
import {
	ONBOARDING_ENABLED,
	getOnboardingTotalSteps,
} from "components/Onboarding/config/onboardingSteps";

/**
 * Drawer de "Configuración general" de la company.
 *
 * Conviven en una sola vista (por secciones) los parámetros globales que irán
 * creciendo poco a poco: el flag de aprobación de ausencias del supervisor,
 * las credenciales del terminal web de fichajes y el reinicio del onboarding.
 *
 * Datos vía GET/PUT companies/config. La respuesta nunca trae el password del
 * terminal, por lo que el campo arranca vacío y sólo se envía con valor.
 */
class GeneralConfigDrawer extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			saving: false,
			loadError: false,
			supervisor_can_approve_own_absences: false,
			web_terminal_email: "",
			web_terminal_password: "",
			restartingOnboarding: false,
		};
	}

	async componentDidMount() {
		const response = await companyService.getConfig();
		if (response.ok) {
			const data = response.data || response;
			this.setState({
				loading: false,
				supervisor_can_approve_own_absences: Boolean(
					data.supervisor_can_approve_own_absences
				),
				web_terminal_email: data.web_terminal_email || "",
			});
		} else {
			this.setState({ loading: false, loadError: true });
		}
	}

	handleToggleSupervisorOwnAbsences = () => {
		this.setState((prev) => ({
			supervisor_can_approve_own_absences:
				!prev.supervisor_can_approve_own_absences,
		}));
	};

	handleChange = (event) => {
		this.setState({ [event.target.id]: event.target.value });
	};

	handleRestartOnboarding = async () => {
		const { t } = this.props;
		if (this.state.restartingOnboarding || this.state.saving) return;

		this.setState({ restartingOnboarding: true });
		const ok = await onboardingStore.restart();
		this.setState({ restartingOnboarding: false });

		if (!ok) {
			toastr.error(t("common.error"), t("common.errorGeneric"));
			return;
		}

		toastr.success(
			t("common.success"),
			t("generalConfigs.onboardingRestarted")
		);
	};

	handleSave = async () => {
		const { t } = this.props;
		const {
			saving,
			supervisor_can_approve_own_absences,
			web_terminal_email,
			web_terminal_password,
		} = this.state;
		if (saving) return;

		this.setState({ saving: true });
		const response = await companyService.updateConfig({
			supervisor_can_approve_own_absences,
			web_terminal_email,
			web_terminal_password,
		});

		if (!response.ok) {
			const errs = errorFormating(response);
			toastr.error("Error", errs.general || t("common.errorSaving"));
			this.setState({ saving: false });
			return;
		}

		toastr.success(t("common.success"), t("common.changesSaved"));
		// El password nunca vuelve del back: limpiamos el campo tras guardar.
		this.setState({ saving: false, web_terminal_password: "" });
		if (typeof this.props.handleClose === "function") this.props.handleClose();
	};

	renderBody() {
		const { t } = this.props;
		const {
			loading,
			loadError,
			saving,
			supervisor_can_approve_own_absences,
			web_terminal_email,
			web_terminal_password,
			restartingOnboarding,
		} = this.state;
		const onboardingTotal = getOnboardingTotalSteps();

		if (loading) return <Loading />;
		if (loadError) {
			return (
				<p className="text-danger">{t("generalConfigs.loadError")}</p>
			);
		}

		return (
			<div className="general-config">
				<section className="general-config__section">
					<h5 className="general-config__section-title">
						{t("generalConfigs.absencesSection")}
					</h5>
					<SwitchField
						controlId="supervisor_can_approve_own_absences"
						isOn={supervisor_can_approve_own_absences}
						onToggle={this.handleToggleSupervisorOwnAbsences}
						label={t("generalConfigs.supervisorOwnAbsences")}
						disabled={saving}
					/>
				</section>

				<section className="general-config__section">
					<h5 className="general-config__section-title">
						{t("generalConfigs.webTerminalSection")}
					</h5>
					<p className="general-config__section-help">
						{isHaddockEnvironment() ? (
							<Trans
								i18nKey="generalConfigs.webTerminalHelpHaddock"
								components={{
									clockinLink: (
										<a
											href="https://hr.haddock.app/clockin/"
											target="_blank"
											rel="noopener noreferrer"
										/>
									),
								}}
							/>
						) : (
							<Trans
								i18nKey="generalConfigs.webTerminalHelpPlain"
								components={{
									clockinLink: (
										<a
											href="https://app.plain.ninja/clockin/"
											target="_blank"
											rel="noopener noreferrer"
										/>
									),
								}}
							/>
						)}
					</p>
					<FormGroup className="form-group" controlId="web_terminal_email">
						<FormLabel>{t("generalConfigs.webTerminalEmail")}</FormLabel>
						<FormControl
							type="email"
							value={web_terminal_email}
							onChange={this.handleChange}
							placeholder={t("generalConfigs.webTerminalEmailPlaceholder")}
							disabled={saving}
							autoComplete="off"
						/>
						<small className="form-text text-muted">
							{t("generalConfigs.webTerminalEmailHint")}
						</small>
					</FormGroup>
					<FormGroup className="form-group" controlId="web_terminal_password">
						<FormLabel>{t("generalConfigs.webTerminalPassword")}</FormLabel>
						<FormControl
							type="password"
							value={web_terminal_password}
							onChange={this.handleChange}
							placeholder={t("generalConfigs.webTerminalPasswordPlaceholder")}
							disabled={saving}
							autoComplete="new-password"
						/>
						<small className="form-text text-muted">
							{t("generalConfigs.webTerminalPasswordHint")}
						</small>
					</FormGroup>
				</section>

				{ONBOARDING_ENABLED && (
					<section className="general-config__section">
						<h5 className="general-config__section-title">
							{t("generalConfigs.onboardingSection")}
						</h5>
						<p className="general-config__section-help">
							{t("generalConfigs.onboardingHelp", {
								total: onboardingTotal,
							})}
						</p>
						<button
							type="button"
							className="btn btn-outline-primary general-config__restart"
							onClick={this.handleRestartOnboarding}
							disabled={saving || restartingOnboarding}
						>
							{restartingOnboarding
								? t("generalConfigs.onboardingRestarting")
								: t("generalConfigs.onboardingRestart")}
						</button>
					</section>
				)}
			</div>
		);
	}

	render() {
		const { t } = this.props;
		const { loading, loadError, saving } = this.state;

		return (
			<Drawer.Main>
				<Drawer.MainHeader
					title={t("generalConfigs.drawerTitle")}
					subtitle={t("generalConfigs.drawerSubtitle")}
				/>
				<Drawer.Body>{this.renderBody()}</Drawer.Body>
				<Drawer.Footer>
					<SubmitButton
						type="button"
						onClick={this.handleSave}
						disabled={loading || loadError}
						isLoading={saving}
						text={t("common.save")}
						loadingText={t("common.saving")}
					/>
				</Drawer.Footer>
			</Drawer.Main>
		);
	}
}

export default withTranslation()(GeneralConfigDrawer);
